import { createContext, useCallback, useContext, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';

interface HeaderExtraContextValue {
  extra: ReactNode;
  claimedId: string | null;
  claim: (id: string) => void;
}

const HeaderExtraContext = createContext<HeaderExtraContextValue | null>(null);

/** Supply one page-level block to the first real page heading in a section. */
export function PageHeaderExtras({ children, extra }: { children: ReactNode; extra: ReactNode }) {
  const [claimedId, setClaimedId] = useState<string | null>(null);
  const claimedRef = useRef<string | null>(null);
  const claim = useCallback((id: string) => {
    if (claimedRef.current) return;
    claimedRef.current = id;
    setClaimedId(id);
  }, []);
  const value = useMemo(() => ({ extra, claimedId, claim }), [extra, claimedId, claim]);
  return <HeaderExtraContext.Provider value={value}>{children}</HeaderExtraContext.Provider>;
}

/** A header claims the extra once; nested section headings cannot duplicate it. */
export function usePageHeaderExtra(): ReactNode {
  const context = useContext(HeaderExtraContext);
  const id = useId();
  useLayoutEffect(() => {
    context?.claim(id);
  }, [context?.claim, id]);
  return context?.claimedId === id ? context.extra : null;
}
